import type {
  OrgMembership,
  OrgMembershipClient,
  OrgMembershipRole,
  OrgMembershipStatus,
} from '../contracts';
import { db } from '@/firebase-config';
import { collectionGroup, doc, getDoc, getDocs, query, where } from 'firebase/firestore';

import { isRecord, optionalString } from './firestore-read-normalizers';

type OrgMembershipDoc = {
  userId?: unknown;
  role?: unknown;
  status?: unknown;
  email?: unknown;
  displayName?: unknown;
  source?: unknown;
};

type MembershipDocumentSnapshot = {
  data: () => unknown;
  ref: {
    parent: {
      parent: { id?: string } | null;
    };
  };
};

function toRole(value: unknown): OrgMembershipRole {
  return value === 'owner' || value === 'admin' || value === 'project-participant'
    ? value
    : 'project-participant';
}

function toStatus(value: unknown): OrgMembershipStatus {
  return value === 'removed' ? 'removed' : 'active';
}

function toMembership(
  membershipDoc: MembershipDocumentSnapshot,
  requestedUserId: string,
): OrgMembership | null {
  const orgId = optionalString(membershipDoc.ref.parent.parent?.id);
  if (!orgId) return null;

  const rawData = membershipDoc.data();
  const data: OrgMembershipDoc = isRecord(rawData) ? rawData : {};

  return {
    orgId,
    userId: optionalString(data.userId) ?? requestedUserId,
    role: toRole(data.role),
    status: toStatus(data.status),
    email: optionalString(data.email),
    displayName: optionalString(data.displayName),
    source: optionalString(data.source),
  };
}

export class FirebaseOrgMembershipClient implements OrgMembershipClient {
  async listUserOrgMemberships(userId: string): Promise<OrgMembership[]> {
    const membershipsSnap = await getDocs(
      query(
        collectionGroup(db, 'memberships'),
        where('userId', '==', userId),
        where('status', '==', 'active'),
      ),
    );

    return membershipsSnap.docs.flatMap((membershipDoc) => {
      const membership = toMembership(membershipDoc as MembershipDocumentSnapshot, userId);
      return membership ? [membership] : [];
    });
  }

  async getActiveOrgIdPreference(userId: string): Promise<string | null> {
    const userSnap = await getDoc(doc(db, 'users', userId));
    const data = userSnap.exists() ? userSnap.data() : {};
    return isRecord(data) ? optionalString(data.activeOrgId) ?? null : null;
  }
}
